import { PropsWithChildren, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import {
  CustomDialogContent,
  CustomDialogFooter,
  CustomDialogTitle,
} from "../ui/custom-dialog";
import { Dialog, DialogHeader } from "../ui/dialog";
import { Form } from "../ui/form";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { useAuth } from "@/context/auth/AuthContext";

type NewItemDialogProps<T extends FieldValues> = PropsWithChildren<{
  title: string;
  onFormSubmit: (data: T, shouldRedirect: boolean) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<T>;
  isLoading?: boolean;
}>;

const NewItemDialog = <T extends FieldValues>({
  children,
  title,
  onFormSubmit,
  open,
  onOpenChange,
  form,
  isLoading = false,
}: NewItemDialogProps<T>) => {
  const { t } = useTranslation();
  const [createAnother, setCreateAnother] = useState(false);
  const { user } = useAuth();
  const isReader = user?.role === "reader" ? true : false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <CustomDialogContent>
        <DialogHeader>
          <CustomDialogTitle>{title}</CustomDialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((data) =>
              onFormSubmit(data, !createAnother),
            )}
          >
            <div className="px-6 flex flex-col gap-6 mb-8 mt-3">
              {children}

              <Label className="flex flex-col gap-4">
                {t("postItemCreationAction")}

                <RadioGroup
                  defaultValue="shared"
                  value={
                    createAnother ? "createAnotherOne" : "goToItemAfterCreation"
                  }
                  onValueChange={(value) => {
                    setCreateAnother(value === "createAnotherOne");
                  }}
                  className="flex flex-row gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="goToItemAfterCreation"
                      id="goToItemAfterCreation"
                    />
                    <Label htmlFor="goToItemAfterCreation">
                      {t("goToItemAfterCreation")}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="createAnotherOne"
                      id="createAnotherOne"
                    />
                    <Label htmlFor="createAnotherOne">
                      {t("createAnotherOne")}
                    </Label>
                  </div>
                </RadioGroup>
              </Label>
            </div>

            <CustomDialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex flex-1 bg-white"
              >
                {t("actionCancel")}
              </Button>
              <Button type="submit" className="flex flex-1" loading={isLoading} disabled={isReader}>
                {t("add")}
              </Button>
            </CustomDialogFooter>
          </form>
        </Form>
      </CustomDialogContent>
    </Dialog>
  );
};

export default NewItemDialog;
